import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import KeycloakContext from "../../keycloakContext";
import {
  getProfessorOnboardingState,
  updateProfessorOnboardingTutorial,
} from "../services/professorOnboardingService.js";
import { getUserRoleFromKeycloak } from "../utils/accessPolicy.js";
import {
  PROFESSOR_TUTORIAL_REFRESH_EVENT,
  PROFESSOR_TUTORIAL_RESTART_EVENT,
  PROFESSOR_TUTORIAL_SESSION_KEY,
  dispatchProfessorTutorialOpenCreate,
  dispatchProfessorTutorialOpenImport,
} from "../utils/professorTutorialEvents.js";
import {
  PROFESSOR_TUTORIAL_CLASSROOM_PATH,
  PROFESSOR_TUTORIAL_PHASES as PHASES,
  PROFESSOR_TUTORIAL_SELECTORS as SELECTORS,
  canProfessorTutorialActivatePhase,
  getProfessorTutorialNextPhase,
  getProfessorTutorialPhaseOrder,
  getProfessorTutorialResetState,
  getProfessorTutorialSafeNavigationTarget,
  getProfessorTutorialSelectorForPhase,
  getProfessorTutorialStepIndex,
  isProfessorTutorialFinalPhase,
} from "../utils/professorTutorialState.js";

const APP_ENTRY_PATH = "/applicatiu";
const CLASSROOM_PATH = PROFESSOR_TUTORIAL_CLASSROOM_PATH;

function StepContent({ body, progress }) {
  return (
    <div className="space-y-3">
      <p className="text-sm leading-6 text-slate-700">{body}</p>
      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">{progress}</div>
    </div>
  );
}

function selectorExists(selector) {
  if (typeof document === "undefined") return false;
  return Boolean(document.querySelector(selector));
}

export default function ProfessorTutorialOverlay() {
  const keycloak = useContext(KeycloakContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [run, setRun] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [phase, setPhase] = useState(PHASES.NAV_AULA);
  const [targetReady, setTargetReady] = useState(false);
  const [requiresClassroomCreation, setRequiresClassroomCreation] = useState(false);

  const role = getUserRoleFromKeycloak(keycloak);
  const isProfessor = keycloak?.authenticated && role === "professor";
  const hasClassrooms = Boolean(state?.hasClassrooms || (state?.classrooms || []).length > 0);
  const skipClassroomCreation = hasClassrooms && !requiresClassroomCreation;

  const loadState = useCallback(async () => {
    if (!isProfessor) {
      setState(null);
      return null;
    }
    setLoading(true);
    try {
      const next = await getProfessorOnboardingState();
      setState(next);
      return next;
    } catch (_) {
      setState(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isProfessor]);

  const syncSessionFlag = useCallback((active) => {
    if (typeof sessionStorage === "undefined") return;
    if (active) {
      sessionStorage.setItem(PROFESSOR_TUTORIAL_SESSION_KEY, "1");
    } else {
      sessionStorage.removeItem(PROFESSOR_TUTORIAL_SESSION_KEY);
    }
  }, []);

  useEffect(() => {
    loadState();
  }, [loadState, location.pathname]);

  useEffect(() => {
    if (!isProfessor) {
      setRun(false);
      setWelcomeOpen(false);
      setRequiresClassroomCreation(false);
      syncSessionFlag(false);
      return;
    }

    const shouldShowWelcome =
      location.pathname === APP_ENTRY_PATH &&
      Boolean(state?.shouldShowTutorial) &&
      !run &&
      !loading;

    setWelcomeOpen(shouldShowWelcome);
  }, [isProfessor, location.pathname, state, run, loading, syncSessionFlag]);

  useEffect(() => {
    syncSessionFlag(run);
    return () => syncSessionFlag(false);
  }, [run, syncSessionFlag]);

  useEffect(() => {
    if (!run || !hasClassrooms) return;
    const classroomCreationPhases = [
      PHASES.CLASSROOM_FORM_CENTER,
      PHASES.CLASSROOM_FORM_NAME,
      PHASES.CLASSROOM_FORM_COURSE,
      PHASES.WAIT_CLASSROOM_CREATED,
    ];
    if (classroomCreationPhases.includes(phase)) {
      setCreateDialogOpen(false);
      setPhase(PHASES.STUDENTS_SECTION);
    }
  }, [run, phase, hasClassrooms]);

  const activeSelector = useMemo(() => {
    if (phase === PHASES.NAV_AULA && location.pathname === CLASSROOM_PATH) {
      return SELECTORS.classroomOverview;
    }
    return getProfessorTutorialSelectorForPhase(phase);
  }, [phase, location.pathname]);

  useEffect(() => {
    if (!run || !isProfessor || typeof document === "undefined") {
      setTargetReady(false);
      return undefined;
    }

    const checkTarget = () => {
      const hasTarget = activeSelector ? selectorExists(activeSelector) : true;
      setTargetReady(
        canProfessorTutorialActivatePhase({
          phase,
          pathname: location.pathname,
          hasTarget,
          createDialogOpen,
          importDialogOpen,
        }) || (phase === PHASES.NAV_AULA && location.pathname === CLASSROOM_PATH && hasTarget)
      );
    };

    checkTarget();
    const frame = window.requestAnimationFrame(checkTarget);
    const observer = new MutationObserver(checkTarget);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [run, isProfessor, phase, location.pathname, activeSelector, createDialogOpen, importDialogOpen]);

  useEffect(() => {
    if (!run || !isProfessor || !targetReady) return;

    if (phase === PHASES.NAV_AULA && location.pathname === CLASSROOM_PATH) {
      setPhase(PHASES.CLASSROOM_OVERVIEW);
      return;
    }

    if (phase === PHASES.CREATE_CLASSROOM_BUTTON && createDialogOpen) {
      setPhase(PHASES.CLASSROOM_FORM_CENTER);
      return;
    }

    if (phase === PHASES.ADD_STUDENTS && importDialogOpen) {
      setPhase(PHASES.CSV_TEMPLATE);
    }
  }, [run, isProfessor, targetReady, phase, location.pathname, createDialogOpen, importDialogOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleRefresh = () => {
      loadState();
    };

    const handleRestart = async () => {
      if (!isProfessor) return;
      const next = await updateProfessorOnboardingTutorial({ action: "start" });
      setState(next);
      setWelcomeOpen(false);
      setCreateDialogOpen(false);
      setImportDialogOpen(false);
      setRequiresClassroomCreation(!Boolean(next?.hasClassrooms || (next?.classrooms || []).length > 0));
      setPhase(PHASES.NAV_AULA);
      setRun(true);
    };

    const handleCreateDialogOpened = () => setCreateDialogOpen(true);
    const handleCreateDialogClosed = () => setCreateDialogOpen(false);
    const handleImportDialogOpened = () => setImportDialogOpen(true);
    const handleImportDialogClosed = () => setImportDialogOpen(false);

    window.addEventListener(PROFESSOR_TUTORIAL_REFRESH_EVENT, handleRefresh);
    window.addEventListener(PROFESSOR_TUTORIAL_RESTART_EVENT, handleRestart);
    window.addEventListener("livingsoils:professor-tutorial-create-opened", handleCreateDialogOpened);
    window.addEventListener("livingsoils:professor-tutorial-create-closed", handleCreateDialogClosed);
    window.addEventListener("livingsoils:professor-tutorial-import-opened", handleImportDialogOpened);
    window.addEventListener("livingsoils:professor-tutorial-import-closed", handleImportDialogClosed);

    return () => {
      window.removeEventListener(PROFESSOR_TUTORIAL_REFRESH_EVENT, handleRefresh);
      window.removeEventListener(PROFESSOR_TUTORIAL_RESTART_EVENT, handleRestart);
      window.removeEventListener("livingsoils:professor-tutorial-create-opened", handleCreateDialogOpened);
      window.removeEventListener("livingsoils:professor-tutorial-create-closed", handleCreateDialogClosed);
      window.removeEventListener("livingsoils:professor-tutorial-import-opened", handleImportDialogOpened);
      window.removeEventListener("livingsoils:professor-tutorial-import-closed", handleImportDialogClosed);
    };
  }, [isProfessor, loadState]);

  const handleStart = async () => {
    const next = await updateProfessorOnboardingTutorial({ action: "start" });
    setState(next);
    setWelcomeOpen(false);
    setCreateDialogOpen(false);
    setImportDialogOpen(false);
    setRequiresClassroomCreation(!Boolean(next?.hasClassrooms || (next?.classrooms || []).length > 0));
    setPhase(PHASES.NAV_AULA);
    setRun(true);
  };

  const resetTutorialUi = useCallback(() => {
    const reset = getProfessorTutorialResetState();
    setRun(reset.run);
    setWelcomeOpen(false);
    setTargetReady(reset.targetReady);
    setCreateDialogOpen(reset.createDialogOpen);
    setImportDialogOpen(reset.importDialogOpen);
    setRequiresClassroomCreation(reset.requiresClassroomCreation);
    setPhase(reset.phase);
  }, []);

  const handleSkip = useCallback(async () => {
    resetTutorialUi();
    try {
      const next = await updateProfessorOnboardingTutorial({ action: "skip" });
      setState(next);
    } catch (_) {
      // Keep the UI usable even if persistence is temporarily unavailable.
    }
  }, [resetTutorialUi]);

  const completeTutorial = useCallback(async () => {
    resetTutorialUi();
    try {
      const next = await updateProfessorOnboardingTutorial({ action: "complete" });
      setState(next);
    } catch (_) {
      // Local cleanup is more important than trapping the user behind Joyride.
    }
  }, [resetTutorialUi]);

  const phaseOrder = useMemo(() => getProfessorTutorialPhaseOrder(skipClassroomCreation), [skipClassroomCreation]);

  const stepIndex = useMemo(() => getProfessorTutorialStepIndex(phase, skipClassroomCreation), [phase, skipClassroomCreation]);

  const steps = useMemo(() => {
    const progress = (current) =>
      t("professorOnboardingTutorial.progress", "Pas {{current}} de {{total}}", { current, total: phaseOrder.length });

    const all = {
      [PHASES.NAV_AULA]: {
        target: SELECTORS.navAula,
        title: t("professorOnboardingTutorial.steps.classrooms.title", "Aules"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.classrooms.content", "Aquí és on podràs crear i gestionar les teves aules i l'alumnat. Fes clic a Aula per continuar.")}
            progress={progress(1)}
          />
        ),
        placement: "bottom",
        disableBeacon: true,
      },
      [PHASES.CLASSROOM_OVERVIEW]: {
        target: SELECTORS.classroomOverview,
        title: t("professorOnboardingTutorial.steps.classroomOverview.title", "Espai de l'aula"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.classroomOverview.content", "Aquest espai mostra les teves aules. Des d'aquí pots crear-ne una, seleccionar-la i gestionar l'alumnat.")}
            progress={progress(2)}
          />
        ),
        placement: "top",
        disableBeacon: true,
      },
      [PHASES.CREATE_CLASSROOM_BUTTON]: {
        target: SELECTORS.createClassroom,
        title: t("professorOnboardingTutorial.steps.createClassroom.title", "Crear una aula"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.createClassroom.content", "Aquest botó obre el formulari real per crear una aula per a un grup d'alumnes.")}
            progress={progress(3)}
          />
        ),
        placement: "left",
        disableBeacon: true,
      },
      [PHASES.CLASSROOM_FORM_CENTER]: {
        target: SELECTORS.classroomCenter,
        title: t("professorOnboardingTutorial.steps.classroomCenter.title", "Codi del centre"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.classroomCenter.content", "Introdueix el codi del centre educatiu associat a aquesta aula. És un camp obligatori.")}
            progress={progress(4)}
          />
        ),
        placement: "right",
        disableBeacon: true,
      },
      [PHASES.CLASSROOM_FORM_NAME]: {
        target: SELECTORS.classroomName,
        title: t("professorOnboardingTutorial.steps.classroomName.title", "Nom de l'aula"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.classroomName.content", "Escriu un nom clar per identificar el grup, per exemple el curs o classe. És obligatori.")}
            progress={progress(5)}
          />
        ),
        placement: "right",
        disableBeacon: true,
      },
      [PHASES.CLASSROOM_FORM_COURSE]: {
        target: SELECTORS.classroomCourse,
        title: t("professorOnboardingTutorial.steps.classroomCourse.title", "Curs"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.classroomCourse.content", "Selecciona el curs del grup. Quan hagis completat els camps, fes servir el botó Crear del formulari; el tutorial continuarà només si l'aula es crea correctament.")}
            progress={progress(6)}
          />
        ),
        placement: "right",
        disableBeacon: true,
      },
      [PHASES.STUDENTS_SECTION]: {
        target: SELECTORS.studentsSection,
        title: t("professorOnboardingTutorial.steps.studentsSection.title", "Gestió de l'alumnat"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.studentsSection.content", "Aquesta és la zona d'alumnat de l'aula seleccionada. Aquí podràs afegir estudiants i revisar-ne el progrés.")}
            progress={progress(skipClassroomCreation ? 3 : 7)}
          />
        ),
        placement: "top",
        disableBeacon: true,
      },
      [PHASES.ADD_STUDENTS]: {
        target: SELECTORS.addStudents,
        title: t("professorOnboardingTutorial.steps.addStudents.title", "Afegir alumnat"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.addStudents.content", "Fes clic al botó amb el símbol + per obrir el flux real d'afegir o importar alumnes.")}
            progress={progress(skipClassroomCreation ? 4 : 8)}
          />
        ),
        placement: "right",
        disableBeacon: true,
      },
      [PHASES.CSV_TEMPLATE]: {
        target: SELECTORS.csvTemplate,
        title: t("professorOnboardingTutorial.steps.csvTemplate.title", "Plantilles d'alumnes"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.csvTemplate.content", "LivingSoils proporciona plantilles CSV i Excel. Descarrega'n una, omple les dades de l'alumnat i mantén les columnes esperades.")}
            progress={progress(skipClassroomCreation ? 5 : 9)}
          />
        ),
        placement: "bottom",
        disableBeacon: true,
      },
      [PHASES.CSV_UPLOAD]: {
        target: SELECTORS.csvUpload,
        title: t("professorOnboardingTutorial.steps.csvImport.title", "Penjar i previsualitzar"),
        content: (
          <StepContent
            body={t("professorOnboardingTutorial.steps.csvImport.content", "Quan seleccionis el fitxer CSV o Excel completat, LivingSoils el validarà i mostrarà una previsualització abans de confirmar la importació. Pots finalitzar el tutorial sense importar alumnes reals ara mateix.")}
            progress={progress(skipClassroomCreation ? 6 : 10)}
          />
        ),
        placement: "top",
        disableBeacon: true,
      },
    };

    return phaseOrder.map((phaseKey) => all[phaseKey]);
  }, [skipClassroomCreation, phaseOrder, t]);

  const handleJoyrideCallback = async (data) => {
    const { action, status, type, index } = data;

    if (status === STATUS.SKIPPED) {
      await handleSkip();
      return;
    }

    if (status === STATUS.FINISHED) {
      await completeTutorial();
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      return;
    }

    if (type !== EVENTS.STEP_AFTER || action !== ACTIONS.NEXT) {
      return;
    }

    if (isProfessorTutorialFinalPhase(phase, skipClassroomCreation) || index === phaseOrder.length - 1) {
      await completeTutorial();
      return;
    }

    if (phase === PHASES.NAV_AULA) {
      const navigationTarget = getProfessorTutorialSafeNavigationTarget(phase);
      if (navigationTarget && location.pathname !== navigationTarget) {
        navigate(navigationTarget);
        return;
      }
      if (targetReady) {
        setPhase(PHASES.CLASSROOM_OVERVIEW);
      }
      return;
    }

    if (phase === PHASES.CREATE_CLASSROOM_BUTTON) {
      dispatchProfessorTutorialOpenCreate();
      return;
    }

    if (phase === PHASES.CLASSROOM_FORM_COURSE) {
      setPhase(PHASES.WAIT_CLASSROOM_CREATED);
      return;
    }

    if (phase === PHASES.ADD_STUDENTS) {
      dispatchProfessorTutorialOpenImport();
      return;
    }

    const nextPhase = getProfessorTutorialNextPhase({ phase, hasClassrooms: skipClassroomCreation });
    if (nextPhase !== phase) {
      setPhase(nextPhase);
    }
  };

  if (!isProfessor || !state) {
    return null;
  }

  return (
    <>
      {welcomeOpen ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 px-4">
          <div className="w-full max-w-xl rounded-[2rem] border border-emerald-200 bg-white p-8 shadow-2xl">
            <div className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-800">
              {t("professorOnboardingTutorial.welcome.badge", "Primer accés")}
            </div>
            <h2 className="mt-4 text-3xl font-semibold text-slate-900">
              {t("professorOnboardingTutorial.welcome.title", "Benvingut/da a Living Soils!")}
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-700">
              {t("professorOnboardingTutorial.welcome.description", "Com a professor/a pots crear aules, afegir-hi els teus alumnes i seguir el progrés de les seves expedicions.")}
            </p>
            <p className="mt-3 text-base font-medium text-slate-900">
              {t("professorOnboardingTutorial.welcome.question", "Vols crear la teva primera aula ara?")}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleStart}
                className="inline-flex items-center justify-center rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800"
              >
                {t("professorOnboardingTutorial.actions.start", "Començar tutorial")}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                {t("professorOnboardingTutorial.actions.skip", "Ara no")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {run ? (
        <Joyride
          key={`${run ? "active" : "idle"}-${phase}-${phaseOrder.length}`}
          steps={steps}
          stepIndex={stepIndex}
          run={run && targetReady && phase !== PHASES.WAIT_CLASSROOM_CREATED}
          continuous
          showSkipButton
          showProgress
          disableScrolling
          spotlightClicks
          callback={handleJoyrideCallback}
          locale={{
            back: t("professorOnboardingTutorial.joyride.back", "Enrere"),
            close: t("professorOnboardingTutorial.joyride.close", "Tancar"),
            last: t("professorOnboardingTutorial.joyride.finish", "Finalitzar tutorial"),
            next: t("professorOnboardingTutorial.joyride.next", "Següent"),
            nextLabelWithProgress: t("professorOnboardingTutorial.joyride.next", "Següent"),
            skip: t("professorOnboardingTutorial.actions.skip", "Ara no"),
          }}
          styles={{
            options: {
              zIndex: 10001,
              overlayColor: "transparent",
              primaryColor: "#047857",
              spotlightPadding: 8,
            },
            tooltip: {
              borderRadius: 24,
              padding: 12,
              boxShadow: "0 24px 64px rgba(15, 23, 42, 0.22)",
            },
            spotlight: {
              backgroundColor: "transparent",
              boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 4px rgba(52, 211, 153, 0.45), 0 0 24px rgba(16, 185, 129, 0.35)",
              borderRadius: 20,
            },
          }}
        />
      ) : null}
    </>
  );
}
